import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useZakeke } from '@zakeke/zakeke-configurator-react';
import { MOBILE_BREAKPOINT } from '../Store';

interface HeaderProps {
	currentMode: 'designer' | 'staging' | 'tryon';
	onModeChange: (mode: 'designer' | 'staging' | 'tryon') => void;
	onClose: () => void;
	isLoadingGenerate?: boolean;
	isInert?: boolean;
	isProductStagingEnabled?: boolean;
	isVtoEnabled?: boolean;
}

export const Header: React.FC<HeaderProps> = ({
	currentMode,
	onModeChange,
	onClose,
	isLoadingGenerate = false,
	isInert = false,
	isProductStagingEnabled = false,
	isVtoEnabled = false,
}) => {
	const { T_static } = useZakeke();
	const T = useMemo(() => ({ _: T_static }), [T_static]);

	return (
		<HeaderContainer {...(isInert ? { inert: 'inert' } : {})}>
			<HeaderContent>
				<MobileSwitch>
					<ModeSwitch role='radiogroup' aria-label={T._('Select mode')}>
						<DesignerOption
							onClick={() => !isLoadingGenerate && onClose()}
							disabled={isLoadingGenerate}
							aria-label={T._('Switch to 3D/AR Viewer')}
							role='radio'
							aria-checked='false'
						>
							{T._('3D/AR Viewer')}
						</DesignerOption>
						{isProductStagingEnabled && (
							<AIOption
								active={currentMode === 'staging'}
								onClick={() => !isLoadingGenerate && onModeChange('staging')}
								disabled={isLoadingGenerate}
								aria-label={T._('AI Lifestyle Scenes mode')}
								role='radio'
								aria-checked={currentMode === 'staging'}
							>
								{T._('AI Lifestyle Scenes')}
							</AIOption>
						)}
						{isVtoEnabled && (
							<AIOption
								active={currentMode === 'tryon'}
								onClick={() => !isLoadingGenerate && onModeChange('tryon')}
								disabled={isLoadingGenerate}
								aria-label={T._('AI Try-on mode')}
								role='radio'
								aria-checked={currentMode === 'tryon'}
							>
								{T._('AI Try-on')}
							</AIOption>
						)}
					</ModeSwitch>
				</MobileSwitch>

				<DesktopSwitch>
					<ModeSwitch role='radiogroup' aria-label={T._('Select mode')}>
						<DesignerOption
							onClick={() => !isLoadingGenerate && onClose()}
							disabled={isLoadingGenerate}
							aria-label={T._('Switch to 3D/AR Viewer')}
							role='radio'
							aria-checked='false'
						>
							{T._('3D/AR Viewer')}
						</DesignerOption>
						{isProductStagingEnabled && (
							<AIOption
								active={currentMode === 'staging'}
								onClick={() => !isLoadingGenerate && onModeChange('staging')}
								disabled={isLoadingGenerate}
								aria-label={T._('AI Lifestyle Scenes mode')}
								role='radio'
								aria-checked={currentMode === 'staging'}
							>
								{T._('AI Lifestyle Scenes')}
							</AIOption>
						)}
						{isVtoEnabled && (
							<AIOption
								active={currentMode === 'tryon'}
								onClick={() => !isLoadingGenerate && onModeChange('tryon')}
								disabled={isLoadingGenerate}
								aria-label={T._('AI Try-on mode')}
								role='radio'
								aria-checked={currentMode === 'tryon'}
							>
								{T._('AI Try-on')}
							</AIOption>
						)}
					</ModeSwitch>
				</DesktopSwitch>
			</HeaderContent>
		</HeaderContainer>
	);
};

const HeaderContainer = styled.div`
	border-bottom: 1px solid #e5e7eb;
	padding: 0.75rem 0;

	@media (max-width: ${MOBILE_BREAKPOINT}px) {
		padding: 0.75rem;
	}
`;

const HeaderContent = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	position: relative;
`;

const MobileSwitch = styled.div`
	flex: 1;
	display: flex;
	justify-content: center;

	@media (min-width: ${MOBILE_BREAKPOINT}px) {
		display: none;
	}
`;

const DesktopSwitch = styled.div`
	display: none;
	flex-shrink: 0;

	@media (min-width: ${MOBILE_BREAKPOINT}px) {
		display: block;
	}
`;

const ModeSwitch = styled.div`
	display: inline-flex;
	padding: 3px;

	@media (max-width: ${MOBILE_BREAKPOINT}px) {
		padding: 2px;
		gap: 4px;
	}
`;

const DesignerOption = styled.button<{ disabled?: boolean }>`
	padding: 8px 20px;
	font-size: 13px;
	font-weight: 500;
	color: #666;
	cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
	border-radius: 9999px;
	transition: all 0.3s ease;
	white-space: nowrap;
	z-index: 1;
	position: relative;
	text-align: center;
	background: transparent;
	opacity: ${(props) => (props.disabled ? '0.5' : '1')};
	pointer-events: ${(props) => (props.disabled ? 'none' : 'auto')};
	border: none;
	outline: none;

	&:hover,
	&:active,
	&:focus {
		background: #0069d3;
		color: white;
	}

	@media (max-width: ${MOBILE_BREAKPOINT}px) {
		padding: 6px 10px;
		font-size: 11px;
		border-radius: 20px;
	}
`;

const AIOption = styled.button<{ active: boolean; disabled?: boolean }>`
	padding: 8px 20px;
	font-size: 13px;
	font-weight: 500;
	color: ${(props) => (props.active ? 'white' : '#666')};
	cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
	border-radius: 9999px;
	transition: all 0.3s ease;
	white-space: nowrap;
	z-index: 1;
	position: relative;
	text-align: center;
	background: ${(props) =>
		props.active
			? 'linear-gradient(135deg, rgb(124, 58, 237) 0%, rgb(168, 85, 247) 50%, rgb(236, 72, 153) 100%)'
			: 'transparent'};
	opacity: ${(props) => (props.disabled ? '0.5' : '1')};
	pointer-events: ${(props) => (props.disabled ? 'none' : 'auto')};
	border: none;
	outline: none;

	&:hover,
	&:active,
	&:focus {
		background: linear-gradient(135deg, rgb(124, 58, 237) 0%, rgb(168, 85, 247) 50%, rgb(236, 72, 153) 100%);
		color: white;
	}

	@media (max-width: ${MOBILE_BREAKPOINT}px) {
		padding: 6px 10px;
		font-size: 11px;
		border-radius: 20px;
	}
`;
